import React from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/joy';
import { motion } from 'framer-motion';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

const SUGGESTED_PROMPTS = [
  {
    icon: <DescriptionOutlinedIcon />,
    title: 'Extract data from invoice',
    prompt: 'Extract key fields like invoice number, date, vendor, line items, and total from the attached PDF invoice.',
    color: 'primary',
  },
  {
    icon: <FactCheckOutlinedIcon />,
    title: 'Verify compliance',
    prompt: 'Check if this document meets our compliance policy requirements and highlight any missing fields.',
    color: 'success',
  },
  {
    icon: <SearchOutlinedIcon />,
    title: 'Analyze contract',
    prompt: 'Review this contract and extract key terms, dates, obligations, and potential risks.',
    color: 'warning',
  },
  {
    icon: <AutoFixHighOutlinedIcon />,
    title: 'Create extraction policy',
    prompt: 'Help me create a new policy to extract structured data from insurance claim documents.',
    color: 'neutral',
  },
];

const PromptCard = ({ icon, title, prompt, color, onClick, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 + index * 0.05, duration: 0.4 }}
  >
    <Button
      variant="outlined"
      color="neutral"
      onClick={() => onClick(prompt)}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 100,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 1,
        textAlign: 'left',
        borderRadius: 'lg',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: `${color}.500`,
          bgcolor: `${color}.softBg`,
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 'md',
          bgcolor: `${color}.softBg`,
          color: `${color}.600`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography
        level="title-sm"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      <Typography
        level="body-xs"
        sx={{
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {prompt}
      </Typography>
    </Button>
  </motion.div>
);

const WelcomeScreen = ({ isCreationMode, onSelectPrompt }) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        maxWidth: 800,
        mx: 'auto',
      }}
    >
      {/* Logo and Title */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Stack alignItems="center" sx={{ mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.vars.palette.primary[400]} 0%, ${theme.vars.palette.primary[600]} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: 'lg',
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography
            level="h2"
            sx={{
              background: (theme) =>
                `linear-gradient(135deg, ${theme.vars.palette.text.primary} 0%, ${theme.vars.palette.neutral[500]} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            {isCreationMode ? 'Start a New Conversation' : 'How can I help you?'}
          </Typography>
          <Typography
            level="body-lg"
            color="neutral"
            sx={{ textAlign: 'center', maxWidth: 500 }}
          >
            {isCreationMode
              ? 'Describe your document extraction needs or attach a PDF to get started.'
              : 'Select a suggestion below or type your own message.'}
          </Typography>
        </Stack>
      </motion.div>

      {/* Suggested Prompts Grid */}
      <Box sx={{ width: '100%', maxWidth: 700, px: { xs: 1, sm: 0 } }}>
        <Typography
          level="body-sm"
          sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}
        >
          Suggestions
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {SUGGESTED_PROMPTS.map((item, idx) => (
            <Grid key={idx} xs={12} sm={6}>
              <PromptCard
                {...item}
                index={idx}
                onClick={onSelectPrompt}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Tips */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
      >
        <Stack
          direction="row"
          gap={3}
          sx={{
            mt: 4,
            p: 2,
            bgcolor: 'neutral.softBg',
            borderRadius: 'lg',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Typography level="body-xs" color="neutral">
            Tip: Use <strong>@policy</strong> to reference policies
          </Typography>
          <Typography level="body-xs" color="neutral">
            Tip: Use <strong>@documents</strong> to attach files from storage
          </Typography>
          <Typography level="body-xs" color="neutral">
            Tip: Drag & drop PDFs directly into the chat
          </Typography>
        </Stack>
      </motion.div>
    </Box>
  );
};

export default WelcomeScreen;
